import React from 'react'
import { useNavigate } from 'react-router-dom'
import { usePlantFilters } from './hooks/usePlantFilters'
import { filterOptions } from './data/filterOptions'
import AppNavBar from '../Header/AppNavBar'

const ChevronDownIcon = () => (
  <svg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' strokeWidth={1.5} stroke='currentColor' className='tw-w-5 tw-h-5'>
    <path strokeLinecap='round' strokeLinejoin='round' d='m19.5 8.25-7.5 7.5-7.5-7.5' />
  </svg>
)

const ChevronUpIcon = () => (
  <svg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' strokeWidth={1.5} stroke='currentColor' className='tw-w-5 tw-h-5'>
    <path strokeLinecap='round' strokeLinejoin='round' d='m4.5 15.75 7.5-7.5 7.5 7.5' />
  </svg>
)

const AdvancedPlantSearch: React.FC = () => {
  const navigate = useNavigate()

  const {
    filters,
    expandedFilters,
    toggleFilter,
    handleFilterChange,
    handleNumericFilterChange,
    clearAllFilters
  } = usePlantFilters(filterOptions, () => undefined, { expandAllInitially: true })

  const handleSearch = () => {
    const searchParams = new URLSearchParams()
    Object.entries(filters).forEach(([key, value]) => {
      if (Array.isArray(value) && value.length > 0) {
        searchParams.set(key, value.join(','))
      } else if (typeof value === 'string' && value !== '') {
        searchParams.set(key, value)
      }
    })
    navigate(`/plants?${searchParams.toString()}`)
  }

  const getTranslatedFilterKey = (filterKey: string) => {
    switch (filterKey) {
      case 'category': return 'Grupa użytkowa'
      case 'form': return 'Forma rośliny'
      case 'growthRate': return 'Siła wzrostu'
      case 'flowerColor': return 'Barwa kwiatów'
      case 'floweringSeason': return 'Pora kwitnienia'
      case 'fruits': return 'Owoce'
      case 'fruitingSeason': return 'Pora owocowania'
      case 'soilPh': return 'Ph podłoża'
      case 'soilType': return 'Rodzaj gleby'
      case 'features': return 'Walory'
      case 'habit': return 'Pokrój'
      case 'leafColor': return 'Barwa liści (igieł)'
      case 'leafType': return 'Zimozieloność liści (igieł)'
      case 'sunExposure': return 'Nasłonecznienie'
      case 'soilMoisture': return 'Wilgotność gleby'
      case 'gardenTypes': return 'Rodzaje ogrodów'
      case 'usage': return 'Zastosowanie'
      case 'hardiness': return 'Strefa mrozoodporności'
      default: return filterKey
    }
  }

  const isHorizontalLayout = (filterKey: string) => {
    return ['floweringSeason', 'fruitingSeason'].includes(filterKey)
  }

  const isTwoColumnLayout = (filterKey: string) => {
    return ['category', 'soilPh', 'soilType', 'features', 'flowerColor', 'leafColor'].includes(filterKey) && !isHorizontalLayout(filterKey)
  }

  const filterElements: JSX.Element[] = []

  Object.entries(filterOptions)?.forEach(([filterKey, filterOptionList]) => {
    if (filterKey === 'dimensions') return
    const isExpanded = expandedFilters && expandedFilters[filterKey]
    const options = Array.isArray(filterOptionList) ? filterOptionList : []

    filterElements.push(
      <div key={filterKey} className='tw-bg-white tw-rounded-lg tw-shadow-md tw-overflow-hidden'>
        <button
          className='tw-w-full tw-bg-neutral-50 hover:tw-bg-neutral-100 tw-px-4 tw-py-3.5 tw-text-neutral-700 tw-border-b tw-border-neutral-200 tw-flex tw-justify-between tw-items-center tw-text-base focus:tw-outline-none'
          onClick={() => toggleFilter(filterKey)}
          aria-expanded={isExpanded}
          aria-controls={`filter-options-${filterKey}`}
        >
          {getTranslatedFilterKey(filterKey)}
          {isExpanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
        </button>
        {isExpanded && (
          <div
            id={`filter-options-${filterKey}`}
            className={`tw-p-4 ${
                      isHorizontalLayout(filterKey)
                          ? 'tw-flex tw-flex-wrap tw-gap-x-4 tw-gap-y-3'
                          : isTwoColumnLayout(filterKey)
                              ? 'tw-grid tw-grid-cols-2 tw-gap-x-4 tw-gap-y-3'
                              : 'tw-space-y-3'
                  }`}
          >
            {options.map((option: any) => (
              <div key={option.id} className='tw-flex tw-items-center tw-gap-x-2'>
                <input
                  type='checkbox'
                  className='tw-h-4 tw-w-4 tw-flex-shrink-0 tw-border-neutral-300 tw-rounded focus:tw-ring-offset-0 tw-text-green-600 focus:tw-ring-1 focus:tw-ring-green-500'
                  id={`${filterKey}-${option.id}`}
                  checked={filters[filterKey as keyof typeof filters]?.includes(option.id)}
                  onChange={() => handleFilterChange(filterKey, option.id)}
                />
                <label
                  className='tw-text-sm tw-font-normal tw-text-neutral-700'
                  htmlFor={`${filterKey}-${option.id}`}
                >
                  {option.label}
                </label>
              </div>
            ))}
          </div>
        )}
      </div>
    )
  })

  const dimensionsFilterKey = 'dimensions'
  const isDimensionsExpanded = expandedFilters && expandedFilters[dimensionsFilterKey as keyof typeof expandedFilters]
  filterElements.push(
    <div key={dimensionsFilterKey} className='tw-bg-white tw-rounded-lg tw-shadow-md tw-overflow-hidden'>
      <button
        className='tw-w-full tw-bg-neutral-50 hover:tw-bg-neutral-100 tw-px-4 tw-py-3.5 tw-text-neutral-700 tw-border-b tw-border-neutral-200 tw-flex tw-justify-between tw-items-center tw-text-base focus:tw-outline-none'
        onClick={() => toggleFilter(dimensionsFilterKey)}
        aria-expanded={isDimensionsExpanded}
        aria-controls={`filter-options-${dimensionsFilterKey}`}
      >
        Wymiary (cm)
        {isDimensionsExpanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
      </button>
      {isDimensionsExpanded && (
        <div id={`filter-options-${dimensionsFilterKey}`} className='tw-p-4 tw-space-y-4'>
          <div>
            <label htmlFor='heightMin' className='tw-block tw-text-xs tw-text-neutral-600 tw-mb-1'>Minimalna wysokość</label>
            <input
              type='number' id='heightMin'
              className='tw-block tw-w-full tw-px-3 tw-py-1.5 tw-bg-white tw-border tw-border-neutral-300 tw-rounded-md tw-text-sm tw-shadow-sm placeholder:tw-text-neutral-400 focus:tw-outline-none focus:tw-border-green-500 focus:tw-ring-1 focus:tw-ring-green-500'
              value={filters.heightMin}
              onChange={(e) => handleNumericFilterChange('heightMin', e.target.value)}
            />
          </div>
          <div>
            <label htmlFor='heightMax' className='tw-block tw-text-xs tw-text-neutral-600 tw-mb-1'>Maksymalna wysokość</label>
            <input
              type='number' id='heightMax'
              className='tw-block tw-w-full tw-px-3 tw-py-1.5 tw-bg-white tw-border tw-border-neutral-300 tw-rounded-md tw-text-sm tw-shadow-sm placeholder:tw-text-neutral-400 focus:tw-outline-none focus:tw-border-green-500 focus:tw-ring-1 focus:tw-ring-green-500'
              value={filters.heightMax}
              onChange={(e) => handleNumericFilterChange('heightMax', e.target.value)}
            />
          </div>
          <div>
            <label htmlFor='spreadMin' className='tw-block tw-text-xs tw-text-neutral-600 tw-mb-1'>Minimalna szerokość</label>
            <input
              type='number' id='spreadMin'
              className='tw-block tw-w-full tw-px-3 tw-py-1.5 tw-bg-white tw-border tw-border-neutral-300 tw-rounded-md tw-text-sm tw-shadow-sm placeholder:tw-text-neutral-400 focus:tw-outline-none focus:tw-border-green-500 focus:tw-ring-1 focus:tw-ring-green-500'
              value={filters.spreadMin}
              onChange={(e) => handleNumericFilterChange('spreadMin', e.target.value)}
            />
          </div>
          <div>
            <label htmlFor='spreadMax' className='tw-block tw-text-xs tw-text-neutral-600 tw-mb-1'>Maksymalna szerokość</label>
            <input
              type='number' id='spreadMax'
              className='tw-block tw-w-full tw-px-3 tw-py-1.5 tw-bg-white tw-border tw-border-neutral-300 tw-rounded-md tw-text-sm tw-shadow-sm placeholder:tw-text-neutral-400 focus:tw-outline-none focus:tw-border-green-500 focus:tw-ring-1 focus:tw-ring-green-500'
              value={filters.spreadMax}
              onChange={(e) => handleNumericFilterChange('spreadMax', e.target.value)}
            />
          </div>
        </div>
      )}
    </div>
  )

  const numColumns = 3
  const columns: JSX.Element[][] = Array.from({ length: numColumns }, () => [])
  filterElements.forEach((element, index) => {
    columns[index % numColumns].push(element)
  })

  return (
    <>
      <AppNavBar rightIcons={[]} />
      <section className='tw-bg-neutral-100 tw-pt-8 tw-pb-32'>
        <div className='tw-max-w-6xl tw-mx-auto tw-px-4'>
          <div className='tw-flex tw-flex-col md:tw-flex-row tw-gap-5'>
            {columns.map((columnItems, colIndex) => (
              <div key={colIndex} className='tw-flex tw-flex-col tw-gap-5 md:tw-w-1/2 lg:tw-flex-1'>
                {columnItems.map(element => element)}
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className='tw-fixed tw-bottom-0 tw-left-0 tw-right-0 tw-bg-white tw-p-4 tw-border-t tw-border-neutral-200 tw-shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.07),_0_-2px_4px_-2px_rgba(0,0,0,0.04)] tw-z-50'>
        <div className='tw-max-w-6xl tw-mx-auto tw-flex tw-justify-center md:tw-justify-end tw-gap-4'>
          <button
            className={`
                      tw-text-center tw-rounded-md tw-shadow-lg tw-transition-colors
                      tw-text-green hover:tw-text-green/80 focus:tw-green
                      tw-px-4 tw-py-2 tw-text-sm lg:tw-px-5 lg:tw-py-2.5 lg:tw-text-base tw-bg-white hover:tw-bg-neutral-50 focus:tw-bg-neutral-100
                    `}
            onClick={clearAllFilters}
          >
            Wyczyść filtry
          </button>
          <button
            className={`
                      tw-text-center tw-rounded-md tw-shadow-sm tw-transition-colors
                      tw-text-white
                      tw-px-4 tw-py-2 tw-text-sm lg:tw-px-5 lg:tw-py-2.5 lg:tw-text-base tw-bg-green-600 hover:tw-bg-green-700 focus:tw-bg-green-700
                    `}
            onClick={handleSearch}
          >
            Wyszukaj rośliny
          </button>
        </div>
      </div>
    </>
  )
}

export default AdvancedPlantSearch
